import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import Notification from '@components/base/Notification/Notification';
import language from "@lang/language";

const ContextNotification = createContext({});

//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////

// Module-level imperative bridge so non-React (class components, helpers) can trigger notifications
let externalShow = null;
let externalHide = null;

/**
 * @typedef {Object} NotifyOptions
 * @property {string} [layout]      - Layout name (e.g. 'Default'). Defaults to 'Default'.
 * @property {string} [color]       - Brand color (e.g. 'color-brand-green-400').
 * @property {string} [text]        - HTML-allowed message text.
 * @property {number} [duration]    - Auto-close timeout in ms. 0 = permanent.
 * @property {string} [confirmText] - Label for the confirm action button.
 * @property {Function} [onConfirm] - Callback fired when the confirm button is pressed.
 * @property {string} [cancelText]  - Label for the cancel action button.
 * @property {Function} [onCancel]  - Callback fired when the cancel button is pressed.
 * @property {Function} [onClose]   - Callback fired when the notification is dismissed.
 */

export const notify = {
	/**
	 * Show a notification with full control over options.
	 * @param {NotifyOptions} options
	 * @returns {string|undefined} The notification id (or undefined if provider not mounted).
	 */
	show: (options) => externalShow && externalShow(options),

	/**
	 * Dismiss a notification by id, or all notifications if no id is passed.
	 * @param {string} [id]
	 */
	hide: (id) => externalHide && externalHide(id),

	/**
	 * Show an error notification.
	 * @param {string} text
	 * @param {NotifyOptions} [options]
	 */
	error: (text, options = {}) => externalShow && externalShow({
		duration: 6000,
		text,
		...options,
	}),

	/**
	 * Show a success notification.
	 * @param {string} text
	 * @param {NotifyOptions} [options]
	 */
	success: (text, options = {}) => externalShow && externalShow({
		duration: 6000,
		text,
		...options,
	}),

	/**
	 * Show a warning notification.
	 * @param {string} text
	 * @param {NotifyOptions} [options]
	 */
	warning: (text, options = {}) => externalShow && externalShow({
		duration: 6000,
		text,
		...options,
	}),

	/**
	 * Show a centered modal confirm dialog with cancel + confirm actions.
	 * Both `confirmText` + `onConfirm` and `cancelText` + `onCancel` are required.
	 * @param {string} text
	 * @param {NotifyOptions} [options]
	 */
	confirm: (text, options = {}) => externalShow && externalShow({
		layout: 'Confirm',
		duration: 0,
		cancelText: language.closeMsg,
		onCancel: {},
		confirmText: language.confirmMsg,
		text,
		...options,
	}),

	/**
	 * Show a centered modal confirm dialog with cancel + confirm actions.
	 * @param {string} text
	 * @param {NotifyOptions} [options]
	 */
	alert: (text, options = {}) => externalShow && externalShow({
		layout: 'Confirm',
		duration: 0,
		cancelText: language.closeMsg,
		onCancel: {},
		text,
		...options,
	}),
};

//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////

let idCounter = 0;
const nextId = () => `n_${++idCounter}_${Date.now()}`;

const ContextNotificationProvider = ({children, color: defaultColor = 'color-brand-orange-400'}) => {

	const [notifications, setNotifications] = useState([]);
	const [menuBottomHeight, setMenuBottomHeight] = useState(null);

	const hide = useCallback((id) => {
		if (id == null) {
			setNotifications([]);
			return;
		}
		setNotifications((prev) => prev.filter((n) => n.id !== id));
	}, []);

	const show = useCallback((options = {}) => {
		const id = nextId();
		// Apply the provider-level default brand color when the caller did not specify one.
		const merged = defaultColor && options.color == null
		  ? {color: defaultColor, ...options}
		  : options;
		setNotifications((prev) => [...prev, {id, ...merged}]);
		return id;
	}, [defaultColor]);

	// Register imperative bridge for non-hook callers
	useEffect(() => {
		externalShow = show;
		externalHide = hide;
		return () => {
			externalShow = null;
			externalHide = null;
		};
	}, [show, hide]);

	// Track MenuBottom height so the stack sticks just above it
	useEffect(() => {
		const target = document.getElementById('menuBottom');
		if (!target) {
			return;
		}
		const update = () => setMenuBottomHeight(target.offsetHeight);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(target);
		window.addEventListener('resize', update);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', update);
		};
	}, []);

	const stackStyle = menuBottomHeight != null ? {bottom: `${menuBottomHeight}px`} : undefined;

	const value = {show, hide};

	return (
	  <ContextNotification.Provider value={value}>
		  {children}
		  <div className="NotificationStack" style={stackStyle}>
			  {notifications.map((n) => {
				  const dismiss = () => hide(n.id);
				  const wrap = (fn) => () => {
					  if (typeof fn === 'function') {
						  fn();
					  }
					  dismiss();
				  };
				  return (
					<Notification
					  key={n.id}
					  visible={true}
					  layout={n.layout}
					  color={n.color}
					  duration={n.duration}
					  text={n.text}
					  onClose={wrap(n.onClose)}
					  confirmText={n.confirmText}
					  onConfirm={n.onConfirm ? wrap(n.onConfirm) : null}
					  cancelText={n.cancelText}
					  onCancel={n.onCancel ? wrap(n.onCancel) : null}
					/>
				  );
			  })}
		  </div>
	  </ContextNotification.Provider>
	);
};

export const useNotificationContext = () => useContext(ContextNotification);

export {ContextNotification, ContextNotificationProvider};
